import { reactive } from 'vue';

type GetFn<T> = () => Promise<T>;
type RejectedCallback = (e: Error) => void;
type FulfilledCallback<T> = (result: T) => void;
type OnRejected = (...callbacks: RejectedCallback[]) => void;
type OnFulfilled<T> = (...callbacks: FulfilledCallback<T>[]) => void;

interface Input<T> {
    get: GetFn<T>;
    loading?: boolean;
    rejected?: RejectedCallback[];
    fulfilled?: FulfilledCallback<T>[];
}

export interface SimpleItemState<T> {
    item: T | null;
    loading: boolean;
}

interface Output<T> {
    state: SimpleItemState<T>;
    fetch: () => Promise<void>;
    onRejected: OnRejected;
    onFulfilled: OnFulfilled<T>;
}

export function useSimpleItem<T>({
                                     get,
                                     loading = true,
                                     rejected = [],
                                     fulfilled = [],
                                 }: Input<T>): Output<T> {
    const rejectedFnList = rejected;
    const fulfilledFnList = fulfilled;
    const onRejected: OnRejected = (...callbacks) => {
        rejectedFnList.push(...callbacks);
    };

    const onFulfilled: OnFulfilled<T> = (...callbacks) => {
        fulfilledFnList.push(...callbacks);
    };

    const state: SimpleItemState<T> = reactive({
        loading,
        item: null,
    });

    async function fetch() {
        state.loading = true;

        try {
            const result = await get();
            state.item = result;
            fulfilledFnList.forEach((fn) => fn(result));
        } catch (e) {
            if (rejectedFnList.length > 0) {
                rejectedFnList.forEach((fn) => fn(e));
            } else {
                throw e;
            }
        } finally {
            state.loading = false;
        }
    }

    return {
        state,
        fetch,
        onRejected,
        onFulfilled,
    };
}